import { c as _c } from "react/compiler-runtime";
import { IcoLayer0116, IcoPlusSmall16 } from '@onefootprint/icons';
import type { TemplateInputProps } from '@onefootprint/ui';
import { Form, IconButton } from '@onefootprint/ui';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CtaSection } from '../../shared/cta-section';
import SettingsSection from '../../shared/settings-section';
import { MAX_CTAS } from '../complete-node.constants';
import type { CompleteFormData } from '../complete-node.types';
import type { PanelState } from './complete-node-form/use-panel-state';

/** A rule's value is typed across the whole form, so narrow it before trimming. */
const filledIn = (value: unknown) => typeof value === 'string' && value.trim() !== '';
type CtaFieldsProps = {
  autocompleteItems: TemplateInputProps['autocompleteItems'];
  canEdit: boolean;
  panel: PanelState;
};
export const CtaFields = t0 => {
  const $ = _c(47);
  const {
    autocompleteItems,
    canEdit,
    panel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "completeNode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const form = useFormContext();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["ctaCount", "ctaLabel", "ctaUrl", "ctaTarget", "secondaryCtaLabel", "secondaryCtaUrl", "secondaryCtaStyle"];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== form.control) {
    t3 = {
      control: form.control,
      name: t2
    };
    $[2] = form.control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [ctaCount, ctaLabel, ctaUrl, ctaTarget, secondaryCtaLabel, secondaryCtaUrl, secondaryCtaStyle] = useWatch(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["ctaLabel", "ctaUrl", "secondaryCtaLabel", "secondaryCtaUrl"];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== form.control) {
    t5 = {
      control: form.control,
      name: t4
    };
    $[5] = form.control;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    errors
  } = useFormState(t5);
  const atMax = ctaCount >= MAX_CTAS;
  let t6;
  if ($[7] !== t) {
    t6 = t(_temp);
    $[7] = t;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== panel) {
    t7 = open => panel.setSectionOpen("buttons", open);
    $[9] = panel;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== atMax || $[12] !== canEdit || $[13] !== panel.addCta || $[14] !== t) {
    t8 = canEdit && !atMax ? <IconButton icon={IcoPlusSmall16} aria-label={t(_temp2)} size="tiny" variant="ghost" rounded={true} onClick={panel.addCta} /> : undefined;
    $[11] = atMax;
    $[12] = canEdit;
    $[13] = panel.addCta;
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== ctaCount || $[17] !== t) {
    t9 = ctaCount === 0 && <p className="text-body-3 text-tertiary">{t(_temp3)}</p>;
    $[16] = ctaCount;
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== autocompleteItems || $[20] !== canEdit || $[21] !== ctaCount || $[22] !== ctaLabel || $[23] !== ctaTarget || $[24] !== ctaUrl || $[25] !== errors || $[26] !== panel || $[27] !== t) {
    t10 = ctaCount > 0 && <CtaSection title={ctaLabel.trim() || t(_temp4, {
      index: 1
    })} badge={t(_temp5)} size="compact" defaultOpen={!ctaLabel && !ctaUrl} isSelected={panel.selected === "primary"} hasError={!!errors.ctaLabel || !!errors.ctaUrl} removeLabel={t(_temp6)} removeBlockedReason={ctaCount > 1 ? t(_temp7) : undefined} onRemove={canEdit ? () => panel.removeCta("primary") : undefined}><Form.TemplateInput name="ctaLabel" label={t(_temp8)} placeholder={t(_temp9)} size="compact" autocompleteItems={autocompleteItems} rules={{
        validate: value => filledIn(value) || t(_temp10)
      }} /><Form.TemplateInput name="ctaUrl" label={t(_temp11)} placeholder={t(_temp12)} size="compact" autocompleteItems={autocompleteItems} rules={{
        validate: value_0 => filledIn(value_0) || t(_temp13)
      }} /><div className="flex flex-col gap-2"><Form.Select name="ctaTarget" label={t(_temp14)} size="compact" disabled={!canEdit} options={[{
          value: "new_tab",
          label: t(_temp15)
        }, {
          value: "same_tab",
          label: t(_temp16)
        }]} /><p className="text-caption-2 text-tertiary">{ctaTarget === "new_tab" ? t(_temp17) : t(_temp18)}</p></div></CtaSection>;
    $[19] = autocompleteItems;
    $[20] = canEdit;
    $[21] = ctaCount;
    $[22] = ctaLabel;
    $[23] = ctaTarget;
    $[24] = ctaUrl;
    $[25] = errors;
    $[26] = panel;
    $[27] = t;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== autocompleteItems || $[30] !== canEdit || $[31] !== ctaCount || $[32] !== errors || $[33] !== panel || $[34] !== secondaryCtaLabel || $[35] !== secondaryCtaStyle || $[36] !== secondaryCtaUrl || $[37] !== t) {
    t11 = ctaCount > 1 && <CtaSection title={secondaryCtaLabel.trim() || t(_temp19, {
      index: 2
    })} badge={secondaryCtaStyle === "link" ? t(_temp20) : t(_temp21)} size="compact" defaultOpen={!secondaryCtaLabel && !secondaryCtaUrl} isSelected={panel.selected === "secondary"} hasError={!!errors.secondaryCtaLabel || !!errors.secondaryCtaUrl} removeLabel={t(_temp22)} onRemove={canEdit ? () => panel.removeCta("secondary") : undefined}><Form.TemplateInput name="secondaryCtaLabel" label={t(_temp23)} placeholder={t(_temp24)} size="compact" autocompleteItems={autocompleteItems} rules={{
        validate: value_1 => filledIn(value_1) || t(_temp25)
      }} /><Form.TemplateInput name="secondaryCtaUrl" label={t(_temp26)} placeholder={t(_temp27)} size="compact" autocompleteItems={autocompleteItems} rules={{
        validate: value_2 => filledIn(value_2) || t(_temp28)
      }} /><div className="flex flex-col gap-2"><Form.Select name="secondaryCtaStyle" label={t(_temp29)} size="compact" disabled={!canEdit} options={[{
          value: "secondary",
          label: t(_temp30)
        }, {
          value: "link",
          label: t(_temp31)
        }]} /><p className="text-caption-2 text-tertiary">{secondaryCtaStyle === "link" ? t(_temp32) : t(_temp33)}</p></div><Form.Select name="secondaryCtaTarget" label={t(_temp34)} size="compact" disabled={!canEdit} options={[{
        value: "new_tab",
        label: t(_temp35)
      }, {
        value: "same_tab",
        label: t(_temp36)
      }]} /></CtaSection>;
    $[29] = autocompleteItems;
    $[30] = canEdit;
    $[31] = ctaCount;
    $[32] = errors;
    $[33] = panel;
    $[34] = secondaryCtaLabel;
    $[35] = secondaryCtaStyle;
    $[36] = secondaryCtaUrl;
    $[37] = t;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== panel.openSections.buttons || $[40] !== t10 || $[41] !== t11 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    t12 = <SettingsSection title={t6} icon={IcoLayer0116} keepMounted={true} open={panel.openSections.buttons} onOpenChange={t7} actions={t8}>{t9}{t10}{t11}</SettingsSection>;
    $[39] = panel.openSections.buttons;
    $[40] = t10;
    $[41] = t11;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  return t12;
};
function _temp($) {
  return $.ctas.title;
}
function _temp2($_0) {
  return $_0.ctas.add;
}
function _temp3($_1) {
  return $_1.ctas.empty;
}
function _temp4($_2) {
  return $_2.ctas.button;
}
function _temp5($_3) {
  return $_3.ctas.badges.primary;
}
function _temp6($_4) {
  return $_4.link.remove;
}
function _temp7($_5) {
  return $_5.ctas.removeBlocked;
}
function _temp8($_6) {
  return $_6.link.label;
}
function _temp9($_7) {
  return $_7.link.labelPlaceholder;
}
function _temp10($_8) {
  return $_8.link.labelRequired;
}
function _temp11($_9) {
  return $_9.link.url;
}
function _temp12($_10) {
  return $_10.link.urlPlaceholder;
}
function _temp13($_11) {
  return $_11.link.urlRequired;
}
function _temp14($_12) {
  return $_12.link.target.label;
}
function _temp15($_13) {
  return $_13.link.target.newTab;
}
function _temp16($_14) {
  return $_14.link.target.sameTab;
}
function _temp17($_15) {
  return $_15.link.target.newTabHint;
}
function _temp18($_16) {
  return $_16.link.target.sameTabHint;
}
function _temp19($_17) {
  return $_17.ctas.button;
}
function _temp20($_18) {
  return $_18.ctas.badges.link;
}
function _temp21($_19) {
  return $_19.ctas.badges.secondary;
}
function _temp22($_20) {
  return $_20.secondaryLink.remove;
}
function _temp23($_21) {
  return $_21.secondaryLink.label;
}
function _temp24($_22) {
  return $_22.secondaryLink.labelPlaceholder;
}
function _temp25($_23) {
  return $_23.secondaryLink.labelRequired;
}
function _temp26($_24) {
  return $_24.secondaryLink.url;
}
function _temp27($_25) {
  return $_25.link.urlPlaceholder;
}
function _temp28($_26) {
  return $_26.secondaryLink.urlRequired;
}
function _temp29($_27) {
  return $_27.secondaryLink.style.label;
}
function _temp30($_28) {
  return $_28.secondaryLink.style.secondary;
}
function _temp31($_29) {
  return $_29.secondaryLink.style.link;
}
function _temp32($_30) {
  return $_30.secondaryLink.style.linkHint;
}
function _temp33($_31) {
  return $_31.secondaryLink.style.secondaryHint;
}
function _temp34($_32) {
  return $_32.link.target.label;
}
function _temp35($_33) {
  return $_33.link.target.newTab;
}
function _temp36($_34) {
  return $_34.link.target.sameTab;
}